import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { isModifiedClick } from "@/lib/link-utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Truck, Package, Gift, ShoppingBag, ShoppingCart, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useCart } from "../hooks/use-cart";
import { formatCredit } from "@/features/orders/utils/format-price";

export interface CartSummaryProps {
  className?: string;
  compact?: boolean; // Mini cart için compact view
  showCheckoutButton?: boolean;
  onCheckout?: () => void;
  onViewCart?: () => void;
}

export const CartSummary: React.FC<CartSummaryProps> = ({
  className,
  compact = false,
  showCheckoutButton = true,
  onCheckout,
  onViewCart,
}) => {
  const { t } = useTranslation();
  const {
    subtotal,
    // tax,
    shipping,
    totalPrice,
    cashback,
    totalItems,
    formatPrice,
    getFreeShippingRemaining,
    isFreeShippingEligible,
    isEmpty,
  } = useCart();

  // Navigasyonu `<Link>` yapar; bu handler yalnızca yan etkiyi (dropdown kapatma vb.)
  // çalıştırır ve modifier'lı tıklamada (yeni sekme) atlanır.
  const handleCheckout = (e: React.MouseEvent) => {
    if (isModifiedClick(e)) return;
    onCheckout?.();
  };

  const handleViewCart = (e: React.MouseEvent) => {
    if (isModifiedClick(e)) return;
    onViewCart?.();
  };

  const freeShippingRemaining = getFreeShippingRemaining();
  const isEligibleForFreeShipping = isFreeShippingEligible();

  if (compact) {
    // 📱 Compact view for mini cart dropdown
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn("bg-muted/30 rounded-lg", className)}
      >
        {/* Free Shipping Progress */}
        {!isEligibleForFreeShipping && freeShippingRemaining > 0 && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">
                {t("cart.freeShippingRemaining")}
              </span>
              <span className="font-medium text-primary">
                {formatPrice(freeShippingRemaining)} {t("cart.remaining")}
              </span>
            </div>
            <div className="w-full bg-border rounded-full h-1.5">
              <div
                className="bg-gradient-to-r from-green-500 to-emerald-500 h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: `${Math.min((subtotal / 500) * 100, 100)}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Price Summary */}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t("cart.subtotal")}:</span>
            <span className="font-medium">{formatPrice(subtotal)}</span>
          </div>
          {/* <div className="flex justify-between">
            <span className="text-muted-foreground">{t("cart.tax")}:</span>
            <span className="font-medium">{formatPrice(tax)}</span>
          </div> */}
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t("cart.shipping")}:</span>
            <span
              className={cn(
                "font-medium",
                shipping === 0 ? "text-indigo-500" : ""
              )}
            >
              {shipping === 0 ? t("cart.freeShipping") : formatPrice(shipping)}
            </span>
          </div>
          <div className="border-t border-tertiary pt-2">
            <div className="flex justify-between font-semibold text-base">
              <span>{t("cart.total")}:</span>
              <span className="text-text-primary">
                {formatPrice(totalPrice)}
              </span>
            </div>
          </div>

          {/* Cashback */}
          {cashback > 0 && (
            <div className="flex items-center justify-between gap-2 bg-[#f0fdf4] dark:bg-green-950/30 rounded-lg px-2.5 py-2 mt-1">
              <div className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-[#17b26a] flex-shrink-0" />
                <span className="text-xs font-medium text-[#17b26a]">
                  {t("cart.cashbackEarn")}
                </span>
              </div>
              <span className="text-xs font-bold text-[#17b26a]">
                +{formatCredit(cashback)}
              </span>
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-2">
          <Button
            asChild
            variant="outline"
            size="md"
            className="w-full hover:text-text-primary hover:bg-bg-secondary hover:border-tertiary"
          >
            <Link to="/cart" onClick={handleViewCart}>
              <ShoppingCart className="h-4 w-4 mr-2" />
              <span className="font-medium text-xs">{t("cart.viewCart")}</span>
            </Link>
          </Button>
          {/* Checkout Button */}
          {showCheckoutButton && !isEmpty && (
            <Button
              asChild
              variant="darkPrimary"
              size="md"
              className={cn(
                "w-full group transition-all duration-300",
                "shadow-lg hover:shadow-xl",
                "border border-black dark:hover:shadow-green-500/10"
              )}
            >
              <Link to="/checkout" onClick={handleCheckout}>
                <span className="font-medium text-xs sm:text-base">
                  {t("cart.checkout")}
                </span>
              </Link>
            </Button>
          )}
        </div>
      </motion.div>
    );
  }

  // 🖥️ Full view for cart page
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={className}
    >
      <Card>
        <CardHeader className="pb-3 sm:pb-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Package className="h-4 w-4 sm:h-5 sm:w-5" />
            {t("cart.cartSummary")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6">
          {/* Item Count */}
          <div className="flex items-center justify-between text-sm sm:text-base">
            <span className="text-muted-foreground">{t("cart.itemCount")}</span>
            <span className="font-medium">
              {totalItems}{" "}
              {totalItems === 1 ? t("cart.product") : t("cart.products")}
            </span>
          </div>

          {/* Price Breakdown */}
          <div className="space-y-2 sm:space-y-3">
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-muted-foreground">
                {t("cart.subtotal")}:
              </span>
              <span className="font-medium">{formatPrice(subtotal)}</span>
            </div>
            {/* <div className="flex justify-between text-sm sm:text-base">
              <span className="text-muted-foreground">{t("cart.tax")}:</span>
              <span className="font-medium">{formatPrice(tax)}</span>
            </div> */}
            <div className="flex justify-between items-center text-sm sm:text-base">
              <span className="text-muted-foreground">
                {t("cart.shipping")}:
              </span>
              <div className="text-right">
                <span
                  className={cn(
                    "font-medium",
                    shipping === 0 ? "text-green-600" : ""
                  )}
                >
                  {shipping === 0
                    ? t("cart.freeShipping")
                    : formatPrice(shipping)}
                </span>
                {shipping === 0 && (
                  <p className="text-xs text-green-600 mt-0.5">
                    {t("cart.freeShippingOver")}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Free Shipping Progress */}
          {!isEligibleForFreeShipping && freeShippingRemaining > 0 && (
            <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Truck className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {t("cart.freeShippingRemaining")}{" "}
                  {formatPrice(freeShippingRemaining)} {t("cart.remaining")}!
                </span>
              </div>
              <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min((subtotal / 500) * 100, 100)}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Shipping Benefits */}
          {isEligibleForFreeShipping && (
            <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-900 dark:text-green-100">
                  {t("cart.freeShippingEarned")}
                </span>
              </div>
            </div>
          )}

          {/* Cashback */}
          {cashback > 0 && (
            <div className="flex items-center justify-between gap-2 bg-[#f0fdf4] dark:bg-green-950/30 rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#17b26a] flex-shrink-0" />
                <span className="text-sm font-medium text-[#17b26a]">
                  {t("cart.cashbackEarn")}
                </span>
              </div>
              <span className="text-sm font-bold text-[#17b26a]">
                +{formatCredit(cashback)}
              </span>
            </div>
          )}

          {/* Total */}
          <div className="border-t border-tertiary pt-3 sm:pt-4">
            <div className="flex justify-between items-center">
              <span className="text-base sm:text-lg font-semibold">
                {t("cart.total")}:
              </span>
              <span className="text-lg sm:text-xl font-bold text-primary">
                {formatPrice(totalPrice)}
              </span>
            </div>
          </div>

          {/* Checkout Button */}
          {showCheckoutButton && !isEmpty && (
            <Button
              asChild
              className={cn(
                "w-full group transition-all duration-300 relative overflow-hidden",
                "bg-gradient-purple-blue hover:bg-gradient-cyan-blue",
                "animate-glow shadow-lg hover:shadow-2xl hover:shadow-blue-500/25",
                "border border-blue-500/20 hover:scale-[1.02] active:scale-[0.98]",
                "text-white font-medium"
              )}
              size="default"
            >
              <Link to="/checkout" onClick={handleCheckout}>
                <ShoppingBag className="h-4 w-4 sm:h-5 sm:w-5 mr-2 group-hover:animate-pulse" />
                <span className="font-medium text-sm sm:text-base">
                  <span className="hidden sm:inline">{t("cart.checkout")}</span>
                  <span className="sm:hidden">{t("cart.checkout")}</span>
                </span>
              </Link>
            </Button>
          )}

          {/* Security Info */}
          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              🔒 {t("cart.securePayment")} • {t("cart.sslProtected")}
            </p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
